import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["appElement", "aria", "children", "className", "footer", "id", "isOpen", "large", "onUpdate", "shouldCloseOnOverlayClick", "showCloseButton", "style", "title", "titleProps", "titleTagName"];
import ReactModal from 'react-modal';
import React, { forwardRef, useState, useRef, useEffect, useImperativeHandle } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { IconClose } from '@op/opds.opux-icons/navigation';
import { deprecated, Button, classNames, useLocale } from '@op/opds.opux';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
const messages = {
  en: {
    close: 'close'
  },
  fi: {
    close: 'sulje'
  },
  sv: {
    close: 'stäng'
  }
};

/**
 * _Modals are overlays that prevent users from interacting with the underlying view until a specific action is taken._
 *
 * ***
 *
 * `<Modal>` is a component that appears over everything else
 * in the application and disables all other application
 * functionality until dismissed.
 *
 * Before using it as a component, you should set `appElement` prop with
 * the root of your application, using a selector like `#root`. This will
 * enable the non-modal contents to be hidden from screen reader users when
 * the modal is open. Don't set `body` as `appElement`, as doing so will hide
 * the modal contents from screen reader users.
 *
 * Note that to be able to reopen the modal after it has been closed, the `onUpdate`
 * prop must be set. This ensures that the open/closed state of the OPUX Modal and
 * the react-modal stay in sync. The react-modal is a library that the Modal
 * internally utilizes. See the examples below how the `onUpdate` is used with
 * the `isOpen` prop and the `setOpen` state function.
 *
 * In addition to using its `isOpen` prop, a modal may be imperatively opened
 * and closed by passing it a `ref` and thereby calling its `open()` and
 * `close()` methods.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping `<div>` element that isn't the overlay.
 */

export const Modal = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      appElement,
      aria,
      children,
      className,
      footer,
      id: propId,
      isOpen,
      large,
      onUpdate,
      shouldCloseOnOverlayClick = false,
      showCloseButton,
      style,
      title,
      titleProps = {},
      titleTagName: TitleTag = 'h2'
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (TitleTag !== 'h2') {
    deprecated('The titleTagName of the Modal is deprecated as of August 2024.');
  }
  // Correct syntax found from this thread: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/35572
  const boxRef = useRef();
  const [stateId] = useState(() => "ds-modal-" + uuidv4());
  const [open, setOpen] = useState(!!isOpen);
  const lc = useLocale();
  const modalId = propId || stateId;
  const titleId = titleProps.id || modalId + "_title";
  useImperativeHandle(ref, () => ({
    boxRef,
    close: () => setOpen(false),
    isOpen: () => open,
    open: () => setOpen(true)
  }));
  useEffect(() => {
    if (typeof isOpen === 'boolean') {
      setOpen(isOpen);
    }
  }, [isOpen]);
  useEffect(() => {
    onUpdate == null || onUpdate({
      open
    });
  }, [open]);
  if (appElement) {
    ReactModal.setAppElement(appElement);
  }
  return /*#__PURE__*/React.createElement(ReactModal, _extends({
    aria: aria && aria.labelledby ? aria : _extends({}, aria, {
      labelledby: title ? titleId : undefined
    }),
    bodyOpenClassName: classNames(buildVersionedClass('ds-modal--open'), buildVersionedClass('ds-modal--prevent-layout-shift')),
    htmlOpenClassName: buildVersionedClass('ds-modal--open'),
    className: buildVersionedClassNames('ds-modal', className, {
      'ds-modal--large': large
    }),
    closeTimeoutMS: 250,
    id: modalId,
    isOpen: open,
    onRequestClose: () => setOpen(false),
    overlayClassName: {
      afterOpen: buildVersionedClass('ds-modal__overlay--enter'),
      base: large ? buildVersionedClass('ds-modal__overlay') + " " + buildVersionedClass('ds-modal__overlay--large') : buildVersionedClass('ds-modal__overlay'),
      beforeClose: buildVersionedClass('ds-modal__overlay--exit')
    },
    ref: boxRef,
    shouldCloseOnOverlayClick: shouldCloseOnOverlayClick,
    style: style
  }, props), /*#__PURE__*/React.createElement("header", {
    className: buildVersionedClass('ds-modal__header')
  }, title && /*#__PURE__*/React.createElement(TitleTag, _extends({}, titleProps, {
    className: classNames(buildVersionedClass('ds-modal__title'), titleProps.className),
    id: titleId
  }), title), showCloseButton && /*#__PURE__*/React.createElement(Button, {
    className: buildVersionedClass('ds-modal__close-button'),
    ghost: true,
    icon: IconClose,
    iconLabel: messages[lc].close,
    iconProps: {
      color: 'default'
    },
    onClick: event => {
      event.preventDefault();
      setOpen(false);
    },
    onKeyDown: event => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        setOpen(false);
      }
    },
    size: "large",
    tabIndex: 0
  })), children && /*#__PURE__*/React.createElement("div", {
    className: buildVersionedClass('ds-modal__body')
  }, children), footer && /*#__PURE__*/React.createElement("div", {
    className: buildVersionedClass('ds-modal__footer')
  }, footer));
});
Modal.displayName = 'Modal';